import {
  Box,
  Button,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { tokens } from "../app/theme";
import { isHttpsUrl, isUuid, parseList } from "./pluginValidation";
import {
  useApplicationConfig,
  usePersistApplicationConfig,
} from "./useApplicationConfig";

interface TenantFormErrors {
  tenantId?: string;
  graphEndpoint?: string;
  aadEndpoint?: string;
  scopes?: string;
}

const API_VERSIONS = ["v1.0", "beta"];

export function MicrosoftTenantPluginForm() {
  const config = useApplicationConfig();
  const persist = usePersistApplicationConfig();
  const tenant = config.plugins.microsoftTenant;

  const [tenantId, setTenantId] = useState(tenant.tenantId);
  const [graphEndpoint, setGraphEndpoint] = useState(tenant.graphEndpoint);
  const [aadEndpoint, setAadEndpoint] = useState(tenant.aadEndpoint);
  const [graphApiVersion, setGraphApiVersion] = useState(
    tenant.graphApiVersion
  );
  const [scopes, setScopes] = useState(tenant.scopes.join("\n"));
  const [errors, setErrors] = useState<TenantFormErrors>({});

  useEffect(() => {
    setTenantId(tenant.tenantId);
    setGraphEndpoint(tenant.graphEndpoint);
    setAadEndpoint(tenant.aadEndpoint);
    setGraphApiVersion(tenant.graphApiVersion);
    setScopes(tenant.scopes.join("\n"));
  }, [tenant]);

  function validate(): TenantFormErrors {
    const next: TenantFormErrors = {};
    if (tenantId.trim() && !isUuid(tenantId)) {
      next.tenantId = "Must be a UUID";
    }
    if (!isHttpsUrl(graphEndpoint)) {
      next.graphEndpoint = "Must be an HTTPS URL";
    }
    if (!isHttpsUrl(aadEndpoint)) {
      next.aadEndpoint = "Must be an HTTPS URL";
    }
    const parsedScopes = parseList(scopes);
    if (parsedScopes.some((scope) => !isHttpsUrl(scope))) {
      next.scopes = "Each scope must be an HTTPS URL";
    }
    return next;
  }

  function save(): void {
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      return;
    }
    persist({
      ...config,
      plugins: {
        ...config.plugins,
        microsoftTenant: {
          ...tenant,
          tenantId: tenantId.trim(),
          graphEndpoint: graphEndpoint.trim().replace(/\/+$/, ""),
          aadEndpoint: aadEndpoint.trim().replace(/\/+$/, ""),
          graphApiVersion,
          scopes: parseList(scopes),
        },
      },
    }).catch(() => {
      /* error already surfaced via app feedback */
    });
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={16} marginTop={2}>
      <TextField
        label="Tenant ID"
        value={tenantId}
        onChange={(event) => setTenantId(event.target.value)}
        error={!!errors.tenantId}
        helperText={errors.tenantId || "Microsoft Entra tenant UUID."}
        variant="outlined"
        inputProps={{ "data-testid": "tenant-id" }}
      />
      <TextField
        label="Graph endpoint"
        value={graphEndpoint}
        onChange={(event) => setGraphEndpoint(event.target.value)}
        error={!!errors.graphEndpoint}
        helperText={errors.graphEndpoint || "HTTPS Microsoft Graph base URL."}
        variant="outlined"
        inputProps={{ "data-testid": "tenant-graph-endpoint" }}
      />
      <TextField
        label="AAD endpoint"
        value={aadEndpoint}
        onChange={(event) => setAadEndpoint(event.target.value)}
        error={!!errors.aadEndpoint}
        helperText={errors.aadEndpoint || "HTTPS Azure AD login base URL."}
        variant="outlined"
        inputProps={{ "data-testid": "tenant-aad-endpoint" }}
      />
      <TextField
        select
        label="Graph API version"
        value={graphApiVersion}
        onChange={(event) => setGraphApiVersion(event.target.value)}
        variant="outlined"
        inputProps={{ "data-testid": "tenant-api-version" }}
      >
        {API_VERSIONS.map((version) => (
          <MenuItem key={version} value={version}>
            {version}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Scopes"
        value={scopes}
        onChange={(event) => setScopes(event.target.value)}
        error={!!errors.scopes}
        helperText={errors.scopes || "One HTTPS scope per line."}
        multiline
        minRows={2}
        variant="outlined"
      />
      <Box>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          data-testid="tenant-save"
        >
          Save tenant settings
        </Button>
      </Box>
      <Box style={{ color: tokens.fg3, fontSize: 12 }}>
        These settings apply to runtime Microsoft Graph calls. Cognito Corporate
        SSO tenant metadata is deploy-time configuration and is not changed here.
      </Box>
    </Box>
  );
}
